import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Providers } from './components/providers'

// Public pages (mirror davidogbueli.org)
import LandingPage from './pages/landing'
const AboutPage = lazy(() => import('./pages/about'));
const MinistryAboutPage = lazy(() => import('./pages/ministry/about'));
const CoreValuesPage = lazy(() => import('./pages/ministry/core-values'));
const StatementOfFaithPage = lazy(() => import('./pages/ministry/statement-of-faith'));
const LeadershipPage = lazy(() => import('./pages/leadership'));
const EventsPage = lazy(() => import('./pages/events'));
const BiographyPage = lazy(() => import('./pages/about/biography'));
const TestimonyPage = lazy(() => import('./pages/about/testimony'));
const VisionPage = lazy(() => import('./pages/about/vision'));
const MediaPage = lazy(() => import('./pages/media'));
const ResourcesPage = lazy(() => import('./pages/resources'));
const ContactPage = lazy(() => import('./pages/contact'));
const GivePage = lazy(() => import('./pages/give'));
const PrivacyPage = lazy(() => import('./pages/privacy'));
const TermsPage = lazy(() => import('./pages/terms'));
const HelpPage = lazy(() => import('./pages/help'));

// Broadcast (NEW feature)
const BroadcastPage = lazy(() => import('./pages/broadcast/index'));
const PublicBroadcastPage = lazy(() => import('./pages/broadcast/public'));
const PrivateBroadcastPage = lazy(() => import('./pages/broadcast/private'));

// Mentorship
const MentorshipPage = lazy(() => import('./pages/features/mentorship'));
const GMTVPage = lazy(() => import('./pages/features/gmtv'));

// Auth pages
const SignInPage = lazy(() => import('./pages/auth/signin'));
const SignUpPage = lazy(() => import('./pages/auth/signup'));
const RegisterPage = lazy(() => import('./pages/auth/register'));
const RequestAccessPage = lazy(() => import('./pages/request-access'));
const MagicLinkRequestPage = lazy(() => import('./pages/auth/magic-link-request'));
const MagicLinkCallbackPage = lazy(() => import('./pages/auth/magic-link-callback'));
const AuthCallbackPage = lazy(() => import('./pages/auth/callback'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/forgot-password'));
const UpdatePasswordPage = lazy(() => import('./pages/auth/update-password'));
const VerifyEmailPage = lazy(() => import('./pages/auth/verify'));
const JoinPage = lazy(() => import('./pages/join/[code]'));

// Internal pages (require auth - not in main public nav)
const DashboardPage = lazy(() => import('./pages/dashboard'));
const StreamsPage = lazy(() => import('./pages/streams'));
const ProfilePage = lazy(() => import('./pages/profile'));
const SettingsPage = lazy(() => import('./pages/settings'));
const StreamDetailPage = lazy(() => import('./pages/streams/[streamId]'));
const CommentsPage = lazy(() => import('./pages/comments'));
const NotificationsPage = lazy(() => import('./pages/notifications'));
const DemoStreamsPage = lazy(() => import('./pages/demo-streams'));
const AccountDeletionRequestPage = lazy(() => import('./pages/account-deletion-request'));

// Admin pages
const AdminDashboardPage = lazy(() => import('./pages/admin'));
const AdminStreamsPage = lazy(() => import('./pages/admin/streams'));
const AdminNewStreamPage = lazy(() => import('./pages/admin/new-stream'));
const AdminStreamDetailPage = lazy(() => import('./pages/admin/streams/[id]'));
const AdminRecordingsPage = lazy(() => import('./pages/admin/recordings'));
const AdminUsersPage = lazy(() => import('./pages/admin/users'));
const AdminUserDetailPage = lazy(() => import('./pages/admin/users/[id]'));
const AdminAuditLogsPage = lazy(() => import('./pages/admin/audit-logs'));
const AdminLiveInputsPage = lazy(() => import('./pages/admin/live-inputs'));
const AdminRequestsPage = lazy(() => import('./pages/admin/requests'));
const AdminAccessRequestsPage = lazy(() => import('./pages/admin/access-requests'));
const AdminWebhooksPage = lazy(() => import('./pages/admin/webhooks'));
const AdminBroadcastsPage = lazy(() => import('./pages/admin/broadcasts'));
const AdminModerationPage = lazy(() => import('./pages/admin/moderation'));
const AdminHeroManagerPage = lazy(() => import('./pages/admin/hero-manager'));
const ModerationReportsPage = lazy(() => import('./pages/moderator/reports'));

// Not found
const NotFoundPage = lazy(() => import('./pages/404'));

// Layouts
import { LandingLayout } from './components/layouts/landing-layout'
import { PublicLayout } from './components/layouts/public-layout'
import { AuthenticatedLayout } from './components/layouts/authenticated-layout'

// Route guards
import { ProtectedRoute } from './components/auth/protected-route'
import { PublicOnlyRoute } from './components/auth/public-only-route'

// Suspense fallback for lazily-loaded routes. Deliberately a fixed-height,
// layout-neutral block: a spinner that collapses to zero height would itself
// cause the layout shift this is meant to avoid (prod CLS has peaked at 0.90).
function RouteFallback() {
  return (
    <div
      className="flex min-h-[60vh] items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading…</span>
    </div>
  )
}

function App() {
  return (
    <Providers>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        {/* Landing page - Public only (redirects authenticated users to dashboard) */}
        <Route path="/" element={<PublicOnlyRoute />}>
          <Route element={<LandingLayout />}>
            <Route index element={<LandingPage />} />
          </Route>
        </Route>

        {/* ═══════════════════════════════════════════════════════════════════
           PUBLIC PAGES — mirrors davidogbueli.org
           These routes are visible to all visitors and appear in the
           public navigation header. Do NOT add internal/admin routes
           here — those go in the PROTECTED section below.
           ═══════════════════════════════════════════════════════════════════ */}
        <Route path="/" element={<PublicLayout hasHeroSection={false} />}>
          {/* About Pastor */}
          <Route path="about" element={<AboutPage />} />
          <Route path="about/biography" element={<BiographyPage />} />
          <Route path="about/testimony" element={<TestimonyPage />} />
          <Route path="about/vision" element={<VisionPage />} />
          <Route path="about/pastor" element={<Navigate to="/about" replace />} />

          {/* Ministry */}
          <Route path="ministry" element={<MinistryAboutPage />} />
          <Route path="ministry/about" element={<MinistryAboutPage />} />
          <Route path="ministry/core-values" element={<CoreValuesPage />} />
          <Route path="ministry/statement-of-faith" element={<StatementOfFaithPage />} />

          {/* Leadership (accessible but not in primary nav) */}
          <Route path="leadership" element={<LeadershipPage />} />

          <Route path="events" element={<EventsPage />} />

          {/* Media */}
          <Route path="media" element={<MediaPage />} />

           {/* Resources */}
           <Route path="resources" element={<ResourcesPage />} />

           {/* Mentorship */}
           <Route path="mentorship" element={<MentorshipPage />} />

           {/* GMTV */}
           <Route path="gmtv" element={<GMTVPage />} />
           <Route path="media/gmtv" element={<GMTVPage />} />

           {/* Contact */}
           <Route path="contact" element={<ContactPage />} />

          {/* Give */}
          <Route path="give" element={<GivePage />} />

          {/* Legal (not in nav, but publicly accessible) */}
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="account-deletion-request" element={<AccountDeletionRequestPage />} />

           {/* Broadcast — public hub and public stream */}
           <Route path="broadcast" element={<BroadcastPage />} />
           <Route path="broadcast/public" element={<PublicBroadcastPage />} />

           {/* Route aliases to match reference site */}
           <Route path="ministries" element={<Navigate to="/ministry" replace />} />
           <Route path="login" element={<Navigate to="/signin" replace />} />
           <Route path="watch" element={<Navigate to="/broadcast" replace />} />
           <Route path="vod" element={<Navigate to="/broadcast" replace />} />
        </Route>

        {/* ═══════════════════════════════════════════════════════════════════
           AUTH PAGES — sign in / sign up / recovery
           Publicly accessible but uses a layout without footer.
           ═══════════════════════════════════════════════════════════════════ */}
        <Route path="/" element={<PublicLayout showFooter={false} hasHeroSection={false} />}>
          <Route path="signin" element={<SignInPage />} />
          <Route path="auth/signin" element={<SignInPage />} />
          <Route path="auth/signup" element={<SignUpPage />} />
          <Route path="auth/register" element={<RegisterPage />} />
          <Route path="auth/magic-link-request" element={<MagicLinkRequestPage />} />
          <Route path="auth/magic-link" element={<MagicLinkCallbackPage />} />
          <Route path="auth/callback" element={<AuthCallbackPage />} />
          <Route path="auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="auth/update-password" element={<UpdatePasswordPage />} />
          <Route path="auth/verify" element={<VerifyEmailPage />} />
          <Route path="request-access" element={<RequestAccessPage />} />
        </Route>

        {/* Invite join page - public */}
        <Route path="/join/:code" element={<JoinPage />} />

        {/* ═══════════════════════════════════════════════════════════════════
           PROTECTED / INTERNAL ROUTES — requires authentication
           These routes are NOT in the public navigation.
           They support internal platform operations: dashboard,
           streams, admin, moderator, etc.
           ═══════════════════════════════════════════════════════════════════ */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AuthenticatedLayout />}>
            {/* Private Broadcast — requires login */}
            <Route path="/broadcast/private" element={<PrivateBroadcastPage />} />

            {/* User dashboard and settings */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />

            {/* Stream routes */}
            <Route path="/streams">
              <Route index element={<StreamsPage />} />
              <Route path=":streamId" element={<StreamDetailPage />} />
            </Route>

            {/* Community */}
            <Route path="/comments" element={<CommentsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />

            {/* Demo (for testing) */}
            <Route path="/demo" element={<DemoStreamsPage />} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/streams">
              <Route index element={<AdminStreamsPage />} />
              <Route path="new" element={<AdminNewStreamPage />} />
              <Route path=":id" element={<AdminStreamDetailPage />} />
            </Route>
            <Route path="/admin/recordings" element={<AdminRecordingsPage />} />
            <Route path="/admin/users">
              <Route index element={<AdminUsersPage />} />
              <Route path=":id" element={<AdminUserDetailPage />} />
            </Route>
            <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
            <Route path="/admin/live-inputs" element={<AdminLiveInputsPage />} />
            <Route path="/admin/requests" element={<AdminRequestsPage />} />
            <Route path="/admin/access-requests" element={<AdminAccessRequestsPage />} />
            <Route path="/admin/webhooks" element={<AdminWebhooksPage />} />
            <Route path="/admin/broadcasts" element={<AdminBroadcastsPage />} />
            <Route path="/admin/moderation" element={<AdminModerationPage />} />
            <Route path="/admin/hero-manager" element={<AdminHeroManagerPage />} />

            {/* Moderator routes */}
            <Route path="/moderator" element={<DashboardPage />} />
            <Route path="/moderator/reports" element={<ModerationReportsPage />} />

            {/* Pastor routes */}
            <Route path="/pastor" element={<DashboardPage />} />
          </Route>
        </Route>

        {/* ═══════════════════════════════════════════════════════════════════
            LOCALE FALLBACK — redirects /en/* to root as a safety net
            This handles any stale URL normalization that may have been missed.
            ═══════════════════════════════════════════════════════════════════ */}
        <Route path="/en" element={<Navigate to="/" replace />} />
        <Route path="/en/*" element={<Navigate to="/" replace />} />

        {/* Catch all route */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Providers>
  )
}

export default App
